import { addDays, startOfDay } from "date-fns";
import { useUpcomingTimelinePreviewQuery } from "web_myplann/Api/NotificationCenter/useUpcomingTimelinePreviewQuery";
import {
	DASHBOARD_PREVIEW_LIMIT,
	DASHBOARD_UPCOMING_WINDOW_DAYS,
} from "web_myplann/Dashboard/constants";
import { useIsDashboardV2 } from "web_myplann/Dashboard/State/dashboardVariantContext";
import {
	TimelineHorizonEnum,
	type TimelineItemResponseDTO as TimelineItem,
	UpcomingTimelineItemTypeFilterEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { formatApiDate } from "web_myplann/Shared/Utils/apiDate";

interface UpcomingPreview {
	items: TimelineItem[];
	hasMore: boolean;
	isLoading: boolean;
}

export const useDashboardUpcomingPreview = (): UpcomingPreview => {
	const isV2 = useIsDashboardV2();
	const dateTo = isV2
		? formatApiDate(
				addDays(startOfDay(new Date()), DASHBOARD_UPCOMING_WINDOW_DAYS),
			)
		: undefined;

	// BE applies the horizon + type filter and returns items already sorted
	// (soonest first), so no client-side filtering/sorting is needed here.
	// Deduplication against the "resolving now" section is not needed either:
	// on the BE the two sets are disjoint by construction (resolving now =
	// unsolved items dated today or earlier, upcoming = items dated after today).
	const { data, isLoading } = useUpcomingTimelinePreviewQuery({
		horizon: isV2 ? TimelineHorizonEnum.HalfYear : TimelineHorizonEnum.Month,
		type: UpcomingTimelineItemTypeFilterEnum.All,
		dateTo,
		limit: DASHBOARD_PREVIEW_LIMIT,
	});

	return {
		items: data?.items ?? [],
		hasMore: data?.meta.hasMore ?? false,
		isLoading,
	};
};
